// Entity-flow resolvers — FCX-26 M1 (docs/plans/MULTIFLOW-STAGE-GRAPH-PLAN-2026-07-15.md).
//
// `useProductionFlow()` answers "which flow?" from the TENANT
// (packhouse_config.production_flow). That is the right answer for tenant-level
// questions (config screens, the dashboard pipeline, provisioning) and the
// WRONG one for a work item: a packhouse running citrus AND berries has one
// flow per DELIVERY, not one per tenant.
//
// These siblings sit beside it — nothing is rewritten:
//
//   useTenantFlows()              which lanes does this packhouse run? (route
//                                 availability, "is this a mixed site?")
//   useEntityFlowKey(commodityId) which lane is THIS work item on? (thresholds,
//                                 section gates, data filters)
//
// Resolution mirrors the server guard (mig 20260715170000) exactly, so client
// and DB can never disagree about who must force-cool:
//
//   flow(entity) = commodities.flow_key           → commodity_templates
//   fallback     = packhouse_config.production_flow → commodity_templates
//
// An entity with no commodity, or a commodity no template claims, resolves to
// the tenant default — i.e. today's behaviour, byte-identical. That fallback is
// the whole safety story of M1: nothing moves until a commodity is claimed.
//
// M2 adds the manifest-shaped sibling (`useFlowManifest(useEntityFlowKey(id))`)
// when the first section gate needs it — not before.

import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { mustWithhold, readState } from './readState'
import { supabase } from '@/lib/supabase'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useCommodities } from '@/hooks/useCommodities'
import { useFlowManifests } from '@/hooks/useCommodityTemplates'
import { useProductionFlow, type ProductionFlow } from '@/lib/useProductionFlow'
import type { FlowManifest } from '@/modules/packhouse/flows/flowManifest'

const STALE = 5 * 60_000

/**
 * A lane's name as the floor says it — PLURAL, because a channel is a stream of
 * fruit ("Blueberries"), not a flow noun. Deliberately not
 * `commodity_templates.label`: that column is singular ("Blueberry") and reads
 * wrong on a channel switcher. Unknown keys fall back to the key itself, so a
 * config-only flow still renders.
 */
const FLOW_LABEL: Record<string, string> = {
  BLUEBERRY: 'Blueberries', STRAWBERRY: 'Strawberries', CITRUS: 'Citrus',
}

export const flowLabel = (flowKey: string): string => FLOW_LABEL[flowKey] ?? flowKey

// ---------------------------------------------------------------------------
// Pure resolution — the rules, unit-tested. The hooks below are thin wrappers
// that feed these from the caches.
// ---------------------------------------------------------------------------

/**
 * A commodity's flow_key, or null when it is unknown/unclaimed → the caller
 * falls back to the tenant default. Mirrors the server's COALESCE(entity,
 * tenant, …) chain (mig 20260715170000).
 */
export function resolveEntityFlowKey(
  commodities: { commodity_id: string; flow_key: string | null }[],
  commodityId: string | null | undefined,
): string | null {
  if (!commodityId) return null
  return commodities.find(c => c.commodity_id === commodityId)?.flow_key ?? null
}

/**
 * The tenant's lane allow-list. An empty/unset `enabled_flows` means "just the
 * default flow" — mig 20260715170000 keeps the column inert until an admin
 * sets it, so a single-flow tenant reads exactly as it did pre-M1. Never
 * returns [] (a caller treating that as "no lanes" would blank the nav).
 */
export function resolveEnabledFlows(
  listed: string[] | null | undefined,
  defaultFlow: string,
): { enabledKeys: string[]; isMixed: boolean } {
  const enabledKeys = listed && listed.length > 0 ? listed : [defaultFlow]
  return { enabledKeys, isMixed: enabledKeys.length > 1 }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * The flow THIS work item runs: its commodity's flow, else the tenant default.
 *
 * Pass the commodity_id the row already carries — `pallets.commodity_id`
 * (snapshot, mig 1032), `runs.commodity_id` (mig 1031), or
 * `deliveries.commodity_id` (mig 20260715170000). Always returns a usable flow
 * key, so callers keep their synchronous contract.
 */
export function useEntityFlowKey(commodityId: string | null | undefined): ProductionFlow {
  const tenantFlow = useProductionFlow()
  const { data: commodities = [] } = useCommodities()
  return useMemo(
    () => resolveEntityFlowKey(commodities, commodityId) ?? tenantFlow,
    [commodities, commodityId, tenantFlow])
}

/**
 * The row-wise sibling of useEntityFlowKey: resolve MANY rows' lanes in one
 * render. Hooks cannot be called in a loop, so a board/register holding batches
 * from several channels needs one resolver rather than one hook per row.
 *
 * Same rules, same fallback (`resolveEntityFlowKey(…) ?? tenantFlow`) — this
 * only changes the arity, never the answer. Stable while the commodity cache
 * is, so callers can put it in useMemo deps.
 */
export function useEntityFlowResolver(): (commodityId: string | null | undefined) => ProductionFlow {
  const tenantFlow = useProductionFlow()
  const { data: commodities = [] } = useCommodities()
  return useCallback(
    (commodityId: string | null | undefined) =>
      resolveEntityFlowKey(commodities, commodityId) ?? tenantFlow,
    [commodities, tenantFlow])
}

/**
 * The by-NAME sibling of useEntityFlowResolver.
 *
 * Not every row carries a commodity_id. A run (`runs.commodity`) and a dispatch
 * line (`mission_dispatch_summary.line_commodity` / `run_commodity`) carry the
 * commodity's NAME as text, and re-fetching an id for each would be a query per
 * screen for data the commodities cache already holds. Matching is
 * case-insensitive and trimmed — the same comparison the line-preset filter in
 * Pallets.tsx uses — because those text columns are operator-entered upstream.
 *
 * Unknown/blank name → the tenant default, i.e. today's behaviour.
 */
export function useEntityFlowResolverByName(): (commodityName: string | null | undefined) => ProductionFlow {
  const tenantFlow = useProductionFlow()
  const { data: commodities = [] } = useCommodities()
  return useCallback(
    (commodityName: string | null | undefined) => {
      const key = commodityName?.trim().toUpperCase()
      if (!key) return tenantFlow
      return commodities.find(c => c.name.trim().toUpperCase() === key)?.flow_key ?? tenantFlow
    },
    [commodities, tenantFlow])
}

/**
 * Which commodity a capture form should treat as being ON THE TRUCK.
 *
 * A mixed packhouse (`enabled_flows` listing more than one lane) receives more
 * than one fruit through one tenant, so a capture form cannot assume the
 * tenant's default flow. This is the whole rule:
 *
 *   · nothing picked yet → the tenant default, so a single-lane site and a
 *     freshly-opened form behave exactly as before;
 *   · a pick that is still on the allow-list → the pick;
 *   · a pick that is NOT (a lane withdrawn while the form sat open, or a value
 *     restored from an older session) → back to the default, never a lane the
 *     tenant no longer runs.
 *
 * Deliberately a pure function taking the resolved list, so the caller can
 * DERIVE it every render instead of seeding state from query data at mount —
 * that seeding is what made a vehicle picker display one value while holding
 * another (#1816), and the same shape would misfile a whole delivery here.
 */
export function intakeFlowKey(
  picked: string,
  enabledKeys: string[],
  defaultFlow: string,
): string {
  return picked && enabledKeys.includes(picked) ? picked : defaultFlow
}

/** Raw per-tenant flow config (mig 20260715170000). */
interface TenantFlowRow {
  production_flow: string | null
  enabled_flows:   string[] | null
}

/**
 * Which lanes this packhouse runs.
 *
 * `anyForceCools` answers "does ANY lane here force-cool?" — the question a
 * route shell must ask on a mixed site, where the tenant's DEFAULT flow no
 * longer decides whether a stage exists. `isMixed` is the one-line "more than
 * one lane?" test M2's nav/lane-switcher work will hang off.
 */
export function useTenantFlows(): {
  defaultFlow:   ProductionFlow
  enabledKeys:   string[]
  isMixed:       boolean
  manifests:     FlowManifest[]
  anyForceCools: boolean
  /**
   * True when the lane list is a FALLBACK, not the packhouse's answer — the
   * config read produced nothing. `enabledKeys` still resolves to the default
   * so every caller keeps working, but a caller about to state the packhouse's
   * production policy as FACT ("this packhouse runs the Citrus flow, so there
   * is nothing to cool here") must check this first. /precool printed exactly
   * that on a failed read.
   */
  flowsUnread:   boolean
  /**
   * True while the first config read is still in flight: `enabledKeys` is the
   * default flow's fallback, not the answer yet. A caller that HIDES something
   * for a lane (rather than states a policy) must not act on it until it is false.
   */
  flowsLoading:  boolean
} {
  const defaultFlow = useProductionFlow()
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null

  const flowsQ = useQuery<TenantFlowRow | null>({
    queryKey: ['tenant-flows', tenantId],
    staleTime: STALE,
    enabled: tenantId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('packhouse_config')
        .select('production_flow, enabled_flows')
        // For a platform admin RLS returns EVERY tenant's row — without this
        // filter maybeSingle() errors and flow detection collapses to default.
        .eq('tenant_id', tenantId)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as TenantFlowRow | null
    },
  })

  const data = flowsQ.data
  const { enabledKeys, isMixed } = resolveEnabledFlows(data?.enabled_flows, defaultFlow)
  const manifests = useFlowManifests(enabledKeys)
  return {
    defaultFlow,
    enabledKeys,
    isMixed,
    manifests,
    anyForceCools: manifests.some(m => m.usesForceCooling),
    flowsUnread:   mustWithhold(flowsQ),
    flowsLoading:  readState(flowsQ) === 'loading',
  }
}
